import { Component, Input, OnInit } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { FriendshipService } from 'src/app/services/relations/relations.service';
import { SocialInteractionNotificationService } from 'src/app/services/notifications/social-interaction-notification.service';
import { FormServiceService } from 'src/app/services/publications/form-service.service';

interface TopFiveCandidate {
  userId: number;
  profileId: number;
  username: string;
  fullName: string;
  photo: string;
  relationId?: number;
  invitationState: 'none' | 'pending' | 'rejected' | 'active';
  verified: boolean;
  selected: boolean;
  initiallySelected: boolean;
}

@Component({
  selector: 'app-profile-top-five-picker',
  templateUrl: './profile-top-five-picker.component.html',
  styleUrls: ['./profile-top-five-picker.component.scss'],
})
export class ProfileTopFivePickerComponent implements OnInit {
  @Input() selectedTopFive: any[] = [];

  friends: TopFiveCandidate[] = [];
  filteredFriends: TopFiveCandidate[] = [];
  search = '';
  loading = true;
  saving = false;

  constructor(
    private modalController: ModalController,
    private friendshipService: FriendshipService,
    private toastController: ToastController,
    private socialNotifications: SocialInteractionNotificationService,
    private formService: FormServiceService,
  ) {}

  ngOnInit(): void {
    this.loadFriends();
  }

  get selectedCount(): number {
    return this.friends.filter((friend) => friend.selected).length;
  }

  close(): void {
    if (!this.saving) {
      this.modalController.dismiss();
    }
  }

  filterFriends(): void {
    const query = this.search.trim().toLowerCase();
    this.filteredFriends = !query
      ? [...this.friends]
      : this.friends.filter((friend) =>
          `${friend.fullName} ${friend.username}`.toLowerCase().includes(query)
        );
  }

  toggleFriend(friend: TopFiveCandidate): void {
    if (this.saving) return;
    if (!friend.selected && this.selectedCount >= 5) {
      this.presentToast('Your Top 5 can contain no more than five friends.');
      return;
    }
    friend.selected = !friend.selected;
  }

  async save(): Promise<void> {
    if (this.saving) return;

    const additions = this.friends.filter(
      (friend) => friend.selected && !friend.initiallySelected
    );
    const removals = this.friends.filter(
      (friend) => !friend.selected && friend.initiallySelected && friend.relationId
    );

    if (!additions.length && !removals.length) {
      await this.modalController.dismiss({ saved: false });
      return;
    }

    this.saving = true;
    try {
      const newInvitations = additions.filter(
        (friend) => friend.invitationState === 'none'
      );

      await Promise.all([
        ...removals.map((friend) =>
          this.friendshipService.deleteRelation(friend.relationId)
        ),
        ...newInvitations.map((friend) =>
          this.friendshipService.createRelation({
            relationUserId: String(friend.userId),
            relationType: '2',
            source: 'top_five',
          })
        ),
      ]);

      const actor = this.formService?.profile;
      await Promise.all(additions.map((friend) =>
        this.socialNotifications.notifySuccessfulInteraction({
          recipientProfileId: friend.profileId,
          remoteTitle: 'Top 5 invitation ⭐',
          remoteBody: `@${actor?.username || 'someone'} selected you for their Top 5.`,
          localTitle: 'Top 5 invitation sent',
          localBody: `@${friend.username} was notified.`,
          extra: {
            interaction_type: 'top_five',
            destination_type: 'profile',
            destination_profile_id: Number(actor?.id || 0),
          },
        })
      ));

      const message = additions.length
        ? 'Top 5 updated. New friends will appear after accepting the invitation.'
        : 'Top 5 updated.';
      await this.presentToast(message);
      await this.modalController.dismiss({ saved: true });
    } catch (error) {
      console.error('Unable to update Top 5.', error);
      await this.presentToast('We could not update your Top 5. Please try again.');
    } finally {
      this.saving = false;
    }
  }

  private loadFriends(): void {
    const selectedIds = new Set(
      (this.selectedTopFive || [])
        .map((friend) => Number(friend?.profile_id || friend?.id || 0))
        .filter(Boolean)
    );

    this.friendshipService.getAllRelations({
      relationDirection: '3',
      relationType: [1],
      relationStatus: '2',
      page: '1',
    }).then((raw: any) => {
      const relations = this.parseRelations(raw);
      this.friends = relations
        .map((relation: any) => {
          const normalized = Array.isArray(relation) ? relation[0] : relation;
          const user = normalized?.user_info;
          const profileId = Number(user?.profile_id || user?.id || 0);
          const superButton = normalized?.buttons_info?.superButton;
          const selected = selectedIds.has(profileId);
          const superButtonText = String(superButton?.text || '').toLowerCase();
          const invitationState: TopFiveCandidate['invitationState'] = selected
            ? 'active'
            : superButtonText.includes('pendiente') || superButtonText.includes('pending')
              ? 'pending'
              : superButtonText.includes('rechazado') || superButtonText.includes('rejected')
                ? 'rejected'
                : 'none';

          return {
            userId: Number(user?.user_id || user?.profile_id || user?.id || 0),
            profileId,
            username: user?.username || '',
            fullName: user?.full_name || user?.name || user?.username || 'Friend',
            photo: this.resolvePhoto(user?.photo),
            relationId: Number(superButton?.relation_id || 0) || undefined,
            invitationState,
            verified: user?.Verificacion === 3
              || user?.Verificacion === '3'
              || user?.Verificacion === true
              || user?.kyc === 'Validado',
            selected,
            initiallySelected: selected,
          } as TopFiveCandidate;
        })
        .filter((friend: TopFiveCandidate) => Boolean(friend.userId && friend.profileId));
      this.filteredFriends = [...this.friends];
    }).catch((error) => {
      console.error('Unable to load friends for Top 5.', error);
      this.friends = [];
      this.filteredFriends = [];
    }).finally(() => {
      this.loading = false;
    });
  }

  private parseRelations(raw: any): any[] {
    let data = raw;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        data = [];
      }
    }
    return Array.isArray(data) ? data : (data?.data || []);
  }

  useDefaultAvatar(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.src = 'assets/icon/usuario.png';
  }

  private resolvePhoto(photo: any): string {
    const candidate = typeof photo === 'string'
      ? photo
      : photo?.url || photo?.route || '';
    if (!candidate) return 'assets/icon/usuario.png';
    if (/^(https?:|data:|blob:|capacitor:)/i.test(candidate)) return candidate;
    return `https://laniakea-multimedia.s3.us-east-1.amazonaws.com/${candidate.replace(/^\/+/, '')}`;
  }

  private async presentToast(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 2800,
      position: 'bottom',
      color: 'dark',
    });
    await toast.present();
  }
}
